import { FlexColumn, FlexRow, MaterialIcon } from '@gorila-shared-ui/components';
import { Block } from 'baseui/block';
import { FlexGrid, FlexGridItem } from 'baseui/flex-grid';
import { ChevronRight } from 'baseui/icon';
import { LabelMedium, ParagraphSmall } from 'baseui/typography';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { DOWNLOADS_PAGE } from '../../constants/app';
import { REPORT_TYPE_OPTIONS } from '../../constants/reports';
import { useStyles } from '../../hooks/useStyles';

function ReportTypeChooser() {
  const { theme, css, classes } = useStyles();
  const { id: workspaceId } = useParams();
  const navigate = useNavigate();
  const cardStyles = ({
    active = true,
    minHeight = '100px',
    hasRoute = true,
    padding = theme.sizing.scale600,
  }: {
    active?: boolean;
    minHeight?: string;
    hasRoute?: boolean;
    padding?: string;
  }) =>
    css({
      padding,
      ...theme.borders.border300,
      borderRadius: theme.borders.popoverBorderRadius,
      height: 'auto',
      minHeight,
      width: '100%',
      cursor: active ? (hasRoute ? 'pointer' : 'auto') : 'not-allowed',
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
      opacity: active ? 1 : 0.6,
      boxSizing: 'border-box',
      ':hover': {
        background: active && hasRoute ? theme.colors.buttonTertiaryHover : undefined,
      },
    });
  const iconWrapperStyles = css({
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    ...theme.borders.border600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.sizing.scale300,
    flexShrink: 0,
  });
  const comingSoonStyles = css({
    background: theme.colors.backgroundInverseSecondary,
    boxSizing: 'border-box',
    color: 'white',
    fontSize: theme.sizing.scale500,
    fontWeight: 'bold',
    left: 0,
    padding: `${theme.sizing.scale200} ${theme.sizing.scale600}`,
    position: 'absolute',
    right: 0,
    textAlign: 'center',
    top: theme.sizing.scale600,
    width: '100%',
  });

  const onReportTypeClick = (route: string) => {
    navigate(generatePath(route, { id: workspaceId }));
  };

  const onGoToDownloads = () => {
    navigate(DOWNLOADS_PAGE.route);
  };

  return (
    <FlexColumn classNames={css({ overflowY: 'auto' })}>
      <FlexGrid
        flexGridColumnCount={[1, 1, 1, 1, 3]}
        flexGridColumnGap="scale600"
        flexGridRowGap="scale600"
        overflow="visible"
      >
        {REPORT_TYPE_OPTIONS.map((reportType) => (
          <FlexGridItem
            display="flex"
            key={reportType.id}
            onClick={
              !reportType.comingSoon && reportType.route ? () => onReportTypeClick(reportType.route!) : undefined
            }
            style={{ position: 'relative' }}
          >
            <Block className={cardStyles({ active: !reportType.comingSoon, hasRoute: !!reportType.route })}>
              <Block className={iconWrapperStyles}>
                <MaterialIcon
                  name={reportType.icon}
                  size="18px"
                  color={theme.colors.contentSecondary}
                />
              </Block>
              <LabelMedium style={{ fontWeight: theme.typography.HeadingSmall.fontWeight }}>
                {reportType.label}
              </LabelMedium>
              {reportType.description && <ParagraphSmall margin={0}>{reportType.description}</ParagraphSmall>}
              {reportType.children && (
                <FlexColumn
                  gap={theme.sizing.scale300}
                  classNames={css({ marginTop: theme.sizing.scale100 })}
                >
                  {reportType.children.map((child) => (
                    <Block
                      key={child.id}
                      className={cardStyles({
                        active: true,
                        hasRoute: true,
                        minHeight: '40px',
                        padding: `${theme.sizing.scale300} ${theme.sizing.scale500}`,
                      })}
                      onClick={() => onReportTypeClick(child.route)}
                    >
                      <FlexRow
                        classNames={classes.centeredBetween}
                        gap={theme.sizing.scale300}
                      >
                        <ParagraphSmall margin={0}>{child.label}</ParagraphSmall>
                        <ChevronRight size={22} />
                      </FlexRow>
                    </Block>
                  ))}
                </FlexColumn>
              )}
            </Block>
            {reportType.comingSoon && <div className={comingSoonStyles}>Próximamente</div>}
          </FlexGridItem>
        ))}
        <FlexGridItem
          display="flex"
          onClick={onGoToDownloads}
        >
          <Block className={cardStyles({ active: true })}>
            <Block className={iconWrapperStyles}>
              <MaterialIcon
                name="cloud_download"
                size="18px"
                color={theme.colors.contentSecondary}
              />
            </Block>
            <LabelMedium style={{ fontWeight: theme.typography.HeadingSmall.fontWeight }}>
              Descargas reportes
            </LabelMedium>
            <ParagraphSmall margin={0}>Revisa y descarga los reportes generados.</ParagraphSmall>
          </Block>
        </FlexGridItem>
      </FlexGrid>
    </FlexColumn>
  );
}

export default ReportTypeChooser;
